import { calculateContributionMarginFromUnitPrice } from '@calculations/price/utilities/contribution-margin';
import {
  calculateGrossMarginFromUnitPrice,
  calculateUnitPriceFromGrossMargin,
} from '@calculations/price/utilities/gross-margin';
import { SnapRoundingOptionsCustom } from '@calculations/round';

import {
  OnGrossMarginChangeInput,
  OnGrossMarginChangeOutput,
  OnUnitCostChangeInput,
  OnUnitCostChangeOutput,
  OnUnitPriceChangeInput,
  OnUnitPriceChangeOutput,
  RecalculatePriceStrategy,
} from '../types';
import { noOpQuantityChange, noOpQuotePriceCreation } from './no-op';

function onUnitCostChange({
  state,
  quotePrice,
  unitCost,
}: OnUnitCostChangeInput): OnUnitCostChangeOutput {
  if (!quotePrice.grossMargin) {
    return {
      status: 'failure',
      reason: 'No gross margin found',
    };
  }

  const unitPrice = calculateUnitPriceFromGrossMargin(
    {
      unitCost,
      grossMargin: quotePrice.grossMargin,
    },
    SnapRoundingOptionsCustom(state),
  );

  const contributionMargin = calculateContributionMarginFromUnitPrice({
    unitVariableCosts: quotePrice.unitVariableCosts!,
    unitPrice,
  });

  return {
    status: 'success',
    unitPrice,
    grossMargin: quotePrice.grossMargin,
    contributionMargin,
  };
}

function onUnitPriceChange({
  quotePrice,
  unitPrice,
}: OnUnitPriceChangeInput): OnUnitPriceChangeOutput {
  if (!quotePrice.unitCost) {
    return {
      status: 'failure',
      reason: 'No unit cost found',
    };
  }

  const grossMargin = calculateGrossMarginFromUnitPrice({
    unitCost: quotePrice.unitCost,
    unitPrice,
  });

  return {
    status: 'success',
    grossMargin,
  };
}

function onGrossMarginChange({
  quotePrice,
  grossMargin,
}: OnGrossMarginChangeInput): OnGrossMarginChangeOutput {
  if (!quotePrice.unitCost) {
    return {
      status: 'failure',
      reason: 'No gross margin found',
    };
  }

  const unitPrice = calculateUnitPriceFromGrossMargin({
    unitCost: quotePrice.unitCost,
    grossMargin,
  });

  const contributionMargin = calculateContributionMarginFromUnitPrice({
    unitVariableCosts: quotePrice.unitVariableCosts!,
    unitPrice,
  });

  return {
    status: 'success',
    unitPrice,
    contributionMargin,
  };
}

export const recalculateNewPartUserCost = {
  onQuantityChange: noOpQuantityChange,
  onUnitPriceChange: onUnitPriceChange,
  onGrossMarginChange: onGrossMarginChange,
  onContributionMarginChange: null,
  onBaseGrossMarginChange: null,
  onQuotePriceCreation: noOpQuotePriceCreation,
  onUnitCostChange,
} satisfies RecalculatePriceStrategy;
